import "/node_modules/flag-icons/css/flag-icons.min.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { BrandRoutes } from "./routes/brand.routes";
import { AffiliateRoutes } from "./routes/affiliate.routes";
import { AgencyRoutes } from "./routes/agency.routes";
import { Toaster } from "react-hot-toast";
import { NotFound } from "./pages/NotFound";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Pricing from "./pages/Pricing";
import Screencast from "./pages/Screencast";
import { PublicBrandDetailView } from "./pages/store/PublicBrandDetails";
import { AdminRoutes } from "./routes/admin.routes";
import { SalesRoutes } from "./routes/sales.routes";
import TermsAndConditions from "./pages/TermsAndConditions";
import "./App.css";
import { AuthRoutes } from "./routes/auth.routes";
import { Login } from "./pages/auth/Login";
import { AuthLayout } from "./pages/auth/Layout";
import { AuthProvider } from "./contexts/AuthContext";
import { AgencyAuthProvider } from "./contexts/AgencyAuthContext";
import { AdminAuthProvider } from "./contexts/AdminAuthContext/provider";
import { AdminLogin } from "./pages/admin/auth/Login";
import { SalesResetPassword } from "./pages/admin/auth/SalesResetPassword";
import { Referral } from "./pages/Referral";
import { AdminInvite } from "./pages/AdminInvite";
import { OnboardingPay } from "./pages/OnboardingPay";
import PublicRewardProfile from "./pages/PublicRewardProfile";
import { JivoChatLoader } from "./components/JivoChatLoader";
import SentryTest from "./pages/SentryTest";
import { SneakylinkRedirect } from "./pages/sneakylink/SneakylinkRedirect";
import { ThemeProvider } from "next-themes";
import { PortalBrandingProvider } from "./contexts/portalBranding";
import { PortalBrandingHead } from "./components/common/PortalBrandingHead";
import { PortalBrandingTheme } from "./components/common/PortalBrandingTheme";
import { PortalHostGate } from "./components/common/PortalHostGate";
import { PortalJoinRoute } from "./components/common/PortalJoinRoute";
import { PortalProgramRoute } from "./pages/PublicProgramPage";
import { UnbrandedSurface } from "./components/common/UnbrandedSurface";

const queryClient = new QueryClient();

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <PortalBrandingProvider>
          {/* White-label title/favicon and accent colours from the hostname;
              no-ops elsewhere. Above the router so the auth pages are
              themed before anyone signs in. */}
          <PortalBrandingHead />
          <PortalBrandingTheme layer="host" />
          {/* An unclaimed subdomain must 404, not fall through to the app. */}
          <PortalHostGate>
          <BrowserRouter>
          <Toaster position="top-right" />
          <AuthProvider>
            <AgencyAuthProvider>
              <JivoChatLoader />
              <Routes>
                <Route path="/" element={<AuthLayout />}>
                  <Route path="/" element={<Login />} />
                </Route>
                {/* Reveshare's own panels: never branded, even on a portal host. */}
                <Route path="/auth/:role/login" element={
                  <UnbrandedSurface title="Reveshare Admin">
                    <AdminAuthProvider>
                      <AuthLayout />
                    </AdminAuthProvider>
                  </UnbrandedSurface>
                }>
                  <Route index element={<AdminLogin />} />
                </Route>
                <Route path="/auth/sales/reset-password" element={
                  <UnbrandedSurface title="Reveshare Admin">
                    <AuthLayout />
                  </UnbrandedSurface>
                }>
                  <Route index element={<SalesResetPassword />} />
                </Route>
                <Route path="/auth/*" element={<AuthRoutes />} />
                <Route path="/affiliate/*" element={<AffiliateRoutes />} />
                <Route path="/brand/*" element={<BrandRoutes />} />
                <Route path="/agency/*" element={<AgencyRoutes />} />
                <Route
                  path="/admin/*"
                  element={
                    <UnbrandedSurface title="Reveshare Admin">
                      <AdminRoutes />
                    </UnbrandedSurface>
                  }
                />
                <Route
                  path="/sales/*"
                  element={
                    <UnbrandedSurface title="Reveshare Sales">
                      <SalesRoutes />
                    </UnbrandedSurface>
                  }
                />
                <Route
                  path="/rewards/public/:token"
                  element={<PublicRewardProfile />}
                />
                <Route
                  path="/store/:username/join"
                  element={<PublicBrandDetailView />}
                />
                {/* The same join page on a brand's own portal, where the
                    subdomain says which brand this is. */}
                <Route path="/join" element={<PortalJoinRoute />} />
                {/* A portal brand's tier program page, open to visitors when
                    the brand made it public. */}
                <Route path="/program" element={<PortalProgramRoute />} />
                <Route path="/referral/:referrerCode" element={<Referral />} />
                <Route path="/admin-invite/:token" element={<AdminInvite />} />
                <Route path="/onboarding/pay/:token" element={<OnboardingPay />} />
                <Route path="/sl/:token" element={<SneakylinkRedirect />} />
                <Route path="/policy" element={<PrivacyPolicy />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/screencast" element={<Screencast />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/debug-sentry" element={<SentryTest />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AgencyAuthProvider>
          </AuthProvider>
          </BrowserRouter>
          </PortalHostGate>
        </PortalBrandingProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
